import React, { useEffect, useState, FC } from "react";
import "./index.scss";
import Dropdown from "../../static/Dropdown/Dropdown";
import { dropdownItem } from "../../static/Dropdown/types";
import {
  getMajorEventsPreviousChampionFilters,
  getMajorEventsPreviousChampion
} from "../../services/tournaments";
import { Event, EventPlayer, Player } from "./types";
import constants from "../../constants";
interface Props {
  tournamentGroupId: string | number;
  pageTitle?: string;
  isTeam: boolean;
}
interface Filters {
  matchTypeCode: dropdownItem[];
  playerTypeCode: dropdownItem[];
  nationCode: dropdownItem[];
}

const filterLabels = {
  nationCode: "National filter"
};

const PastChampionsOlympic: FC<Props> = ({
  tournamentGroupId,
  pageTitle,
  isTeam
}) => {
  const [isLoading, setLoading] = useState<boolean>(false);
  let [isListLoading, setIsListLoading] = useState<boolean>(false);
  let [filter, setFilter] = useState<
    Record<string, dropdownItem | null | undefined>
  >({
    playerTypeCode: undefined,
    matchTypeCode: undefined,
    nationCode: undefined
  });
  let [filters, setFilters] = useState<Filters>({
    playerTypeCode: [],
    matchTypeCode: [],
    nationCode: []
  });
  const [results, setResults] = useState<Event[]>([]);

  const isParalympic = () => location.pathname.indexOf("paralympic") >= 0;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const emptyItem = {
          label: "All",
          value: ""
        };
        const filters = await getMajorEventsPreviousChampionFilters({
          tournamentGroupId
        });
        const { MatchTypes, PlayerTypes, NationalityCodes } = filters;
        const matchTypeCode = MatchTypes.map(
          ({ code, name }: { code: string; name: string }) => ({
            label: name,
            value: code
          })
        );
        const playerTypeCode = PlayerTypes.map(
          ({ code, name }: { code: string; name: string }) => ({
            label: name,
            value: code
          })
        );
        const nationCode = NationalityCodes.map((code: string) => ({
          label: code,
          value: code
        }));
        nationCode.unshift(emptyItem);
        setFilter(prev => ({
          ...prev,
          playerTypeCode: playerTypeCode[0],
          matchTypeCode: matchTypeCode[0],
          nationCode: undefined
        }));
        setFilters(prev => ({
          ...prev,
          playerTypeCode,
          matchTypeCode,
          nationCode
        }));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!Object.values(filter).some(item => item && item.value)) return;
      setIsListLoading(true);
      const values = Object.keys(filter).reduce((list, key) => {
        const field = filter[key];
        list[key] = field && field.value;
        return list;
      }, {});
      try {
        const results = await getMajorEventsPreviousChampion({
          tournamentGroupId,
          circuitCode: isParalympic() ? "WCT" : null,
          ...values
        });
        setResults(results);
      } finally {
        setIsListLoading(false);
      }
    })();
  }, [filter]);

  const getLabelText = (label: string) => {
    const field = filter[label];
    if (!field) return "All";
    const text = filters[label].find(
      (item: dropdownItem) => item.value === field.value
    );
    return text ? text.label : "All";
  };

  const PlayerInner = ({
    player,
    nationCode
  }: {
    player?: Player | null;
    nationCode?: string | null;
  }) => {
    if (!player || !nationCode) return null;
    return (
      <span>
        <div className={`itf-flags itf-flags--${nationCode}`}>
          {!constants.omitNationality.includes(nationCode) ? nationCode : null}
        </div>
        <div>
          <a
            href={
              player.profileLink
                ? `${player.profileLink}activity?event=${
                    isParalympic() ? "paralympics" : "olympics"
                  }`
                : "#"
            }
          >
            <div className="name">
              {player.givenName ? player.givenName : null}{" "}
              {player.familyName ? player.familyName : null}
            </div>
          </a>
        </div>
      </span>
    );
  };

  const Player = ({
    nationCode,
    player1,
    nation2Code,
    player2,
    Nation3Code,
    player3,
    Nation4Code,
    player4
  }: EventPlayer) => {
    return (
      <div className="past-champions__player data auto-height">
        <PlayerInner player={player1} nationCode={nationCode} />
        <PlayerInner player={player2} nationCode={nation2Code} />
        <PlayerInner player={player3} nationCode={Nation3Code} />
        <PlayerInner player={player4} nationCode={Nation4Code} />
      </div>
    );
  };

  const Filters = () => {
    return (
      <div className="past-champions__filters-container">
        <div className="past-champions__filters-list">
          {Object.keys(filters).map((label: string) => (
            <Dropdown
              key={label}
              placeholder={`${
                filterLabels[label] ? `${filterLabels[label]}: ` : ""
              }${getLabelText(label)}`}
              options={filters[label]}
              value={filter[label] ? filter[label] : undefined}
              className="past-champions__filters-field"
              onChange={(value: dropdownItem) => {
                setFilter(current => {
                  return { ...current, [label]: value };
                });
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`past-champions ${
        isLoading || isListLoading ? "page-loading" : ""
      }`}
    >
      {!isLoading ? (
        <div className="past-champions">
          <div className="container">
            <div className="past-champions__wrapper">
              <div className="past-champions__header">
                {pageTitle ? <h3>{pageTitle.toLocaleUpperCase()}</h3> : null}
                <Filters />
              </div>
              {results.length > 0 ? (
                <div className="past-champions__table mobile-scrolled">
                  <div>
                    <div
                      className={`table${isTeam ? " isTeam" : " isNotTeam"}`}
                    >
                      <div className="thead">
                        <div className="past-champions__labels row">
                          <div className="past-champions__labels-event data">
                            <span>Event</span>
                          </div>
                          <div className="past-champions__labels-gold data">
                            <span>Gold</span>
                          </div>
                          <div className="past-champions__labels-silver data">
                            <span>Silver</span>
                          </div>
                          <div className="past-champions__labels-bronze data">
                            <span>Bronze</span>
                          </div>
                        </div>
                      </div>
                      <div className="tbody">
                        {results.map(
                          (
                            {
                              tournamentName,
                              tournamentLink,
                              winner,
                              runnerUp,
                              thirdPlace
                            },
                            key
                          ) => (
                            <div className="row-container" key={key}>
                              <div className="row nation">
                                <div className="past-champions__event data">
                                  <span>
                                    <a href={tournamentLink}>
                                      {tournamentName}
                                    </a>
                                  </span>
                                </div>
                                <Player {...winner} />
                                <Player {...runnerUp} />
                                <Player {...thirdPlace} />
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="message-box empty">
                  <p>No players available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PastChampionsOlympic;
